import { ThemeProvider } from '@mui/material'
import { defaultTheme } from './theme'
import { styled } from '@mui/material/styles'
import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'

import IntlProvider from './components/intl-provider/IntlProvider'
import Dashboard from './components/dashboard/DashboardContainer'
import LoginPage from './components/login-page/LoginPageContainer'
import OrganizationPicker from './components/organization-picker/OrganizationPicker'
import { OTAProgressProvider } from './contexts/OTAProgressContext'
import { GlobalLoadingProvider } from './contexts/GlobalLoadingContext'
import { getSelectedOrganization } from './services/api/resolveApiBaseUrl'
import { getOrganizationById } from './config/organizations'
import OrganizationStorage from './services/storage/organization-storage/OrganizationStorage'
import { performLogoutCleanup } from './services/auth/performLogoutCleanup'
import {
  initUpdater,
  checkForUpdateOnLaunch,
  clearAllBundles,
  notifyAppReady,
  startResumeUpdateChecks,
  setSafeToReloadCheck,
  promoteStagedBundle,
} from './ota'
import { store } from './services/store'
import { initPush } from './push/initPush'
import { loggerService } from './services/logger/loggerService'


const PageContent = styled(Dashboard)({
  maxWidth: 1024,
  margin: '0 auto',
  padding: '0 13px',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
})

function App() {

  const needsOrganizationSelection = Capacitor.isNativePlatform() && !getSelectedOrganization()

  const handleOrganizationSelected = async (organizationId: string) => {
    // The Select only ever offers ids from getOrganizations(), but this is the
    // single write path into OrganizationStorage — reject anything else so a
    // production build can never end up with e.g. a stale "development" id
    // persisted (which getSelectedOrganization would then just re-reject anyway,
    // reopening the picker, but silently ignoring here is more direct).
    if (!getOrganizationById(organizationId)) {
      return
    }

    // Existing native installs may already hold an AuthStorage token from before this
    // feature shipped. Without this, picking an org reloads straight into a session
    // issued by whatever backend was in use before, now pointed at a DIFFERENT host —
    // at best a 401, at worst a token that happens to validate on both. No-op when
    // there's nothing to clear (the common case: a genuinely first launch).
    //
    // Left to propagate (not caught here) so the picker's submit handler can reset
    // its in-flight state and let the seller retry — swallowing it here would leave
    // Continue disabled forever with no way to recover. OrganizationStorage.set only
    // runs after a successful cleanup, never before.
    await performLogoutCleanup()
    OrganizationStorage.set(organizationId)
    window.location.reload()
  }

  useEffect(() => {
    // Registered before the chain below, so it is in place by the time any update
    // check can consult it.
    //
    // Apply a downloaded bundle immediately only while nobody is logged in — on the
    // login screen there is no order, form, modal or print in flight, so a reload
    // costs nothing and the update lands in seconds instead of waiting for a cold
    // start. Once logged in this returns false and we fall back to next-launch.
    //
    // isRequesting matters as much as isLogged: REQUEST_LOGIN sets isRequesting while
    // leaving isLogged false, so for the whole duration of an in-flight login the
    // naive !isLogged check would still read "safe" and reload out from under it —
    // killing the request before the epic writes basic.auth to AuthStorage, which
    // loses the issued token and makes the seller type their credentials again.
    setSafeToReloadCheck(() => {
      const { isLogged, isRequesting } = store.getState().Auth
      return !isLogged && !isRequesting
    })

    // Sequenced deliberately: promoting a staged bundle reloads the webview, so
    // anything started alongside it would be torn down mid-flight. (clearAllBundles is
    // not the risk here — capgo refuses to delete the current or the "next" bundle,
    // observed as "Cannot delete the next bundle" — the ordering is about the reload.)
    void (async () => {
      // Bootstrap OTA updater as specified in requirements
      initUpdater(process.env.REACT_APP_OTA_CHANNEL || 'production')

      // MUST come before anything that can reload. It confirms the bundle currently
      // running booted successfully; capgo starts a ~10s watchdog and rolls the bundle
      // back if it never arrives. Promoting first means set() reloads before this call
      // is reached, the watchdog fires, capgo reverts, and the revert re-promotes —
      // an endless reload loop that renders as a blank screen.
      await notifyAppReady().catch(() => {
        // Swallow errors - non-fatal
      })

      // May reload the webview, so nothing below is guaranteed to run.
      await promoteStagedBundle()

      // Clear all stored bundles to prevent old version issues
      await clearAllBundles().catch(() => {
        // Swallow errors - non-fatal
      })

      // Inside the chain on purpose: launched in parallel it could set() a bundle
      // while promoteStagedBundle is activating another one.
      await checkForUpdateOnLaunch().catch(() => {
        // Swallow errors - non-fatal as specified
      })

      // Android apps are rarely killed, so a cold-start-only check can leave a device
      // on stale code for days. Re-check on every foreground; the OTA cooldown throttles it.
      await startResumeUpdateChecks()
    })()


    // Initialize push notifications on mount (safe bootstrap with no side effects)
    void initPush().catch((error) => {
      console.error('[App] Failed to initialize push notifications:', error)
      // Terminal handler for the push bootstrap — initPush re-throws into here, so
      // this is the only place it should be escalated. No push means missed orders.
      loggerService.error(error)
    })


    // Version 1.0.0+18 - OTA progress bar testing
  }, [])


  return (
    <ThemeProvider theme={defaultTheme}>
      <GlobalLoadingProvider>
        <OTAProgressProvider>
          <IntlProvider>
            {needsOrganizationSelection ? (
              <OrganizationPicker onSelect={handleOrganizationSelected} />
            ) : (
              <>
                <PageContent />
                <LoginPage />
              </>
            )}
          </IntlProvider>
        </OTAProgressProvider>
      </GlobalLoadingProvider>
    </ThemeProvider>
  )
}

export default App
